import { FC, useContext, useEffect } from "react";
import { Box, Typography, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useProperty } from "@services";
import { ToastMessageContext } from "@context/toast-message-cotnext";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import LinkingComponent from "./LinkingComponent";
import SmallBooking from "@assets/svg/smallBooking.svg?react";

interface IProps {
  hotelId: string;
  propertyId: number;
  subHotelId: string;
  onDone: () => void;
  onBack?: () => void;
  propertyType?: "collected" | "separated";
}

const BookingSubmitStatus: FC<IProps> = ({
  hotelId,
  propertyId,
  subHotelId,
  onDone,
  propertyType,
  onBack,
}) => {
  const { t, i18n } = useTranslation();
  const { showError } = useContext(ToastMessageContext);
  const {
    UpdateBookingChannelRequestMutation,
    connectAirbnbPropertyToBookingMutation,
  } = useProperty();
  const isArabic = i18n.language === "ar";

  // For separated flow: use UpdateBookingChannelRequestMutation
  const {
    mutate: mutateSeparated,
    isPending: isPendingSeparated,
    isSuccess: isSuccessSeparated,
    isError: isErrorSeparated,
    error: errorSeparated,
    reset: resetSeparated,
  } = UpdateBookingChannelRequestMutation({
    onSuccess: () => {},
    onError: (e: any) => {
      const errorMessage =
        e?.response?.data?.errorMessages?.[0]?.messageEn ||
        e?.response?.data?.errorMessages?.[0]?.messageAr ||
        t("BOOKING.ERROR_CONNECTING");
    },
  });

  // For collected flow: use connectAirbnbPropertyToBookingMutation
  const {
    mutate: mutateCollected,
    isPending: isPendingCollected,
    isSuccess: isSuccessCollected,
    isError: isErrorCollected,
    error: errorCollected,
    reset: resetCollected,
  } = connectAirbnbPropertyToBookingMutation({
    onSuccess: () => {},
    onError: (e: any) => {
      const errorMessage =
        e?.response?.data?.errorMessages?.[0]?.messageEn ||
        e?.response?.data?.errorMessages?.[0]?.messageAr ||
        t("BOOKING.ERROR_CONNECTING");
    },
  });

  useEffect(() => {
    if (propertyType === "separated") {
      // Separated flow: call new API
      mutateSeparated({
        hotelId,
        propertyId,
        subHotelId,
      });
    } else if (propertyType === "collected") {
      // Collected flow: call old API
      mutateCollected({
        hotelId,
        propertyId,
      });
    }
  }, [
    hotelId,
    propertyId,
    subHotelId,
    mutateSeparated,
    mutateCollected,
    propertyType,
  ]);

  // Determine loading/success/error state based on flow type
  const isPending =
    propertyType === "collected" ? isPendingCollected : isPendingSeparated;
  const isSuccess =
    propertyType === "collected" ? isSuccessCollected : isSuccessSeparated;
  const isError =
    propertyType === "collected" ? isErrorCollected : isErrorSeparated;
  const error = propertyType === "collected" ? errorCollected : errorSeparated;
  const reset = propertyType === "collected" ? resetCollected : resetSeparated;
  const mutate =
    propertyType === "collected" ? mutateCollected : mutateSeparated;

  // Loading State (both flows)
  if (isPending) {
    return <LinkingComponent channelIcon={<SmallBooking />} />;
  }

  // Error State (both flows)
  if (isError) {
    const errorMessage = isArabic
      ? (error as any)?.response?.data?.errorMessages?.[0]?.messageAr ||
        t("BOOKING.ERROR_CONNECTING")
      : (error as any)?.response?.data?.errorMessages?.[0]?.messageEn ||
        t("BOOKING.ERROR_CONNECTING");

    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 4,
          pt: 8,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
          }}
        >
          {/* Error Icon */}
          <CancelIcon
            sx={{
              width: "80px",
              height: "80px",
              color: "#EF4444",
            }}
          />

          {/* Error Message */}
          <Typography
            sx={{
              fontFamily: "Tajawal",
              fontSize: "20px",
              fontWeight: 400,
              lineHeight: "24px",
              color: "#0F172A",
              textAlign: "center",
            }}
          >
            {errorMessage}
          </Typography>

          {/* Retry Button */}
          <div className="flex gap-2">
            {/* <Button
              size="small"
              variant="contained"
              onClick={() => onBack && onBack()}
              sx={{
                backgroundColor: "#6B7280",
                borderRadius: "43px",
                padding: "12px 32px",
                fontSize: "16px",
                fontWeight: 700,
                fontFamily: "Tajawal",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#4B5563",
                },
              }}
            >
              {t("COMMON.BACK")}
            </Button> */}
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                if (propertyType === "collected") {
                  resetCollected();
                  mutateCollected({ hotelId, propertyId });
                } else {
                  resetSeparated();
                  mutateSeparated({ hotelId, propertyId, subHotelId });
                }
              }}
              sx={{
                backgroundColor: "#5B3FFF",
                borderRadius: "43px",
                padding: "12px 32px",
                fontSize: "16px",
                fontWeight: 700,
                fontFamily: "Tajawal",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#4A32CC",
                },
              }}
            >
              {t("BOOKING.RETRY")}
            </Button>
          </div>
        </Box>
      </Box>
    );
  }

  // Success State
  if (isSuccess) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 4,
          pt: 8,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
          }}
        >
          {/* Success Icon */}
          <CheckCircleIcon
            sx={{
              width: "80px",
              height: "80px",
              color: "#16AE26",
            }}
          />

          {/* Success Message */}
          <Typography
            sx={{
              fontFamily: "Tajawal",
              fontSize: "20px",
              fontWeight: 400,
              lineHeight: "24px",
              color: "#0F172A",
              textAlign: "center",
            }}
          >
            {t("BOOKING.CONNECTION_SUCCESS")}
          </Typography>

          {/* Done Button */}
          <Button
            variant="contained"
            onClick={onDone}
            sx={{
              backgroundColor: "#5B3FFF",
              borderRadius: "43px",
              padding: "12px 32px",
              fontSize: "16px",
              fontWeight: 700,
              fontFamily: "Tajawal",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#4A32CC",
              },
            }}
          >
            {t("BOOKING.MANAGE_PROPERTIES")}
          </Button>
        </Box>
      </Box>
    );
  }

  return null;
};

export default BookingSubmitStatus;
